/**
 * HoyPage — Vista rápida del día: cargas de hoy por embarcación + KPIs.
 * Muy útil para el piloto en móvil: ver que ya cargó (y no repetir por error)
 * y para Fernando revisar el pulso del día.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Chip,
} from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const hoyStr = () => new Date().toISOString().slice(0, 10);

export default function HoyPage() {
  const empresaId = getEmpresaId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tot, setTot] = useState({ galones: 0, pagado: 0 });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const dia = hoyStr();
      const r = await fetch(
        `${API_BASE}/turismo/combustible/cargas?empresa_id=${empresaId}&fecha_desde=${dia}&fecha_hasta=${dia}&limit=200`,
        { headers: getAuthHeaders() }
      );
      if (r.ok) {
        const d = await r.json();
        setRows(d.items || []);
        setTot({ galones: d.total_galones || 0, pagado: d.total_pagado || 0 });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar por embarcación
  const porEmb = {};
  rows.forEach(c => {
    const k = c.embarcacion_nombre || 'Sin embarcación';
    (porEmb[k] = porEmb[k] || []).push(c);
  });

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #dc2626' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Cargas hoy
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
              {rows.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #f59e0b' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Galones
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
              {Number(tot.galones).toLocaleString('es-CO', { maximumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #16a34a' }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
              Total pagado hoy
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
              {fmtMoney(tot.pagado)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#dc2626' }} />
        </Box>
      ) : Object.keys(porEmb).length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <LocalGasStationIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            Sin cargas registradas hoy.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(porEmb).map(([nombre, cargas]) => {
            const totEmb = cargas.reduce((a, c) => a + Number(c.total_pagado || 0), 0);
            const galEmb = cargas.reduce((a, c) => a + Number(c.galones || 0), 0);
            return (
              <Paper key={nombre} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <DirectionsBoatIcon sx={{ color: '#0d9488' }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                    {nombre}
                  </Typography>
                  <Chip label={`${cargas.length} carga${cargas.length !== 1 ? 's' : ''}`} size="small"
                    sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 11 }}
                  />
                  <Chip label={`${galEmb.toFixed(2)} gal`} size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 11 }}
                  />
                  <Chip label={fmtMoney(totEmb)} size="small"
                    sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {cargas.map(c => (
                    <Box key={c.id} sx={{
                      display: 'flex', gap: 1, alignItems: 'center', py: 0.5,
                      borderBottom: '1px dashed #e2e8f0', fontSize: 12,
                    }}>
                      <Typography sx={{ fontSize: 11, color: '#64748b', minWidth: 50 }}>
                        {new Date(c.fecha_carga).toLocaleTimeString('es-CO',
                          { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </Typography>
                      <Typography sx={{ fontSize: 12, flex: 1 }}>
                        {c.estacion_nombre || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        {Number(c.galones).toFixed(2)} gal
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 90, textAlign: 'right' }}>
                        {fmtMoney(c.total_pagado)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
